import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, User, MapPin, Heart, Bell, Moon, Shield, Trash2, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import { updateProfile, getInterests, getCampuses } from '../lib/api';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

export default function SettingsPage() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [campuses, setCampuses] = useState([]);
  const [availableInterests, setAvailableInterests] = useState([]);

  const [form, setForm] = useState({
    initials: user?.initials || '',
    campus: user?.campus || 'Edinburgh',
    interests: user?.interests || [],
  });

  const [notifications, setNotifications] = useState({
    eventReminders: true,
    chatMessages: true,
    eventCancelled: true,
    upvotes: true,
    reviews: true,
    quietHoursStart: '23:00',
    quietHoursEnd: '08:00',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [intRes, campRes] = await Promise.all([getInterests(), getCampuses()]);
      setAvailableInterests(intRes.data);
      setCampuses(campRes.data);
    } catch (error) {
      console.error('Failed to load data');
    }
  };

  const toggleInterest = (interest) => {
    setForm(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSaveProfile = async () => {
    if (!form.initials || form.initials.length < 1 || form.initials.length > 3) {
      toast.error('Initials must be 1-3 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await updateProfile({
        initials: form.initials.toUpperCase(),
        campus: form.campus,
        interests: form.interests,
      });
      updateUser(res.data);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    }
    setLoading(false);
  };

  const handleDeleteAccount = () => {
    // In production, call API to delete account
    logout();
    navigate('/');
    toast.success('Account deleted');
  };

  return (
    <div className="min-h-screen bg-transparent">
      {/* Header */}
      <header className="sticky top-0 z-10 glass border-b border-border/50">
        <div className="flex items-center gap-4 p-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold">Settings</h1>
        </div>
      </header>

      <div className="p-6 space-y-6 max-w-lg mx-auto">
        {/* Profile Settings */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="initials">Display Initials</Label>
                <Input
                  id="initials"
                  data-testid="settings-initials-input"
                  value={form.initials}
                  onChange={(e) => setForm(prev => ({ ...prev, initials: e.target.value.slice(0, 3).toUpperCase() }))}
                  placeholder="AB"
                  className="uppercase"
                  maxLength={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Campus</Label>
                <Select 
                  value={form.campus} 
                  onValueChange={(v) => setForm(prev => ({ ...prev, campus: v }))}
                >
                  <SelectTrigger data-testid="settings-campus-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {campuses.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Interests</Label>
                <div className="flex flex-wrap gap-2">
                  {availableInterests.map(interest => (
                    <Badge
                      key={interest}
                      variant={form.interests.includes(interest) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleInterest(interest)}
                    >
                      {interest}
                    </Badge>
                  ))}
                </div>
              </div>

              <Button 
                onClick={handleSaveProfile} 
                disabled={loading}
                className="w-full"
                data-testid="save-profile-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Notification Settings */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary" />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'eventReminders', label: 'Event Reminders', desc: '1 hour before events' },
                { key: 'chatMessages', label: 'Chat Messages', desc: 'New messages in groups' },
                { key: 'eventCancelled', label: 'Event Cancelled', desc: 'When events are cancelled' },
                { key: 'upvotes', label: 'Upvote Milestones', desc: 'Every 10 upvotes' },
                { key: 'reviews', label: 'Reviews', desc: 'When you receive reviews' },
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between">
                  <div>
                    <Label htmlFor={item.key}>{item.label}</Label>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                  <Switch
                    id={item.key}
                    checked={notifications[item.key]}
                    onCheckedChange={(v) => setNotifications(prev => ({ ...prev, [item.key]: v }))}
                  />
                </div>
              ))}

              <Separator />

              <div className="space-y-2">
                <Label>Quiet Hours</Label>
                <p className="text-xs text-muted-foreground">No notifications during these hours</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={notifications.quietHoursStart}
                    onChange={(e) => setNotifications(prev => ({ ...prev, quietHoursStart: e.target.value }))}
                    className="flex-1"
                  />
                  <span className="text-muted-foreground">to</span>
                  <Input
                    type="time"
                    value={notifications.quietHoursEnd}
                    onChange={(e) => setNotifications(prev => ({ ...prev, quietHoursEnd: e.target.value }))}
                    className="flex-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Privacy & Safety */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Privacy & Safety
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button variant="outline" className="w-full justify-start">
                Blocked Users
              </Button>
              <Button variant="outline" className="w-full justify-start">
                Privacy Policy
              </Button>
              <Button variant="outline" className="w-full justify-start">
                Terms & Conditions
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Danger Zone */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card className="border-red-300 bg-red-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="w-full bg-red-500 hover:bg-red-600" data-testid="delete-account-btn">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Account
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete your account and remove all your data.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteAccount} className="bg-destructive hover:bg-destructive/90">
                      Delete Account
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </motion.div>

        {/* Spacer for bottom nav */}
        <div className="h-24"></div>
      </div>

      <BottomNav />
    </div>
  );
}
